import { camelCase, keyBy } from 'lodash-es'
import { useQuery } from 'react-query'

import {
  type ApiResponse,
  HTTPMethod,
  unauthenticatedRequest,
} from '../../global/api'
import { getAbsoluteUrl } from '../../sharedLegacy/utils'
import { presentationOptionsDefaults } from '../constants/defaults'
import type { PersonalVideoDTO } from '../model/PersonalVideo'
import type {
  LandingPageOptionsDTO,
  LandingPageSettingsDTO,
} from '../model/LandingPage'
import type { WidgetDTO } from '../model/Widget'
import {
  CACHE_TIME_10_MIN,
  CACHE_TIME_30_MIN,
} from '../components/video-details-page/constants'

import type { VideoPreviewResolvedSettings } from './useVideoPreviewSettings'

type VideoWithMetrics = ApiResponse<PersonalVideoDTO[]>

export const usePublicVideoQuery = (
  videoId: number,
  videoToken: string,
  siteUrl: string
) => {
  return useQuery({
    queryKey: ['public', 'video', videoId, videoToken],
    queryFn: async () => {
      const response = await unauthenticatedRequest<VideoWithMetrics>(siteUrl, {
        url: '/api/2/photo/list',
        method: HTTPMethod.GET,
        data: {
          photo_id: videoId,
          token: videoToken,
        },
      })
      return response.data[0] ?? null
    },
    cacheTime: CACHE_TIME_10_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

export const usePublicWidgetsQuery = (
  videoId: number,
  videoToken: string,
  siteUrl: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ['public', 'widgets', videoId, videoToken],
    enabled,
    queryFn: async () => {
      const response = await unauthenticatedRequest<ApiResponse<WidgetDTO[]>>(
        siteUrl,
        {
          url: '/api/2/video/widget/list',
          method: HTTPMethod.GET,
          data: {
            photo_id: videoId,
            token: videoToken,
          },
        }
      )
      return response.data
    },
    cacheTime: CACHE_TIME_30_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

type SettingsMap = Record<string, { value: unknown }>

export const usePublicSettingsQuery = (siteUrl: string) => {
  return useQuery<SettingsMap>({
    queryKey: ['public', 'settings', siteUrl],
    retry: false,
    queryFn: async () => {
      try {
        const [optionsResponse, settingsResponse] = await Promise.all([
          unauthenticatedRequest<ApiResponse<LandingPageOptionsDTO[]>>(
            siteUrl,
            {
              url: '/api/2/presentation/option/list',
              method: HTTPMethod.GET,
            }
          ),
          unauthenticatedRequest<ApiResponse<LandingPageSettingsDTO[]>>(
            siteUrl,
            {
              url: '/api/2/setting/list',
              method: HTTPMethod.GET,
            }
          ),
        ])

        const options = keyBy(optionsResponse.data, (s) => camelCase(s.key))
        options.personalPromoBanner = {
          key: 'personal-promo-banner',
          type: 'boolean',
          value:
            settingsResponse.data.find(
              (s) => s.setting === 'show_personal_video_page_promo_p'
            )?.value === 1,
          name: 'Personal Promo Banner',
        }

        return { ...presentationOptionsDefaults, ...options }
      } catch (e) {
        // Settings endpoint may require auth — fall back to defaults.
        console.warn(
          '[usePublicSettingsQuery] settings fetch failed, using defaults:',
          e
        )
        return presentationOptionsDefaults as unknown as SettingsMap
      }
    },
    cacheTime: CACHE_TIME_30_MIN,
    staleTime: CACHE_TIME_10_MIN,
  })
}

export const buildPublicSettings = (
  settingsData: SettingsMap | undefined,
  siteUrl: string
): VideoPreviewResolvedSettings => {
  const get = <T>(key: string, fallback: T): T => {
    const v = settingsData?.[key]?.value
    return v !== undefined ? (v as T) : fallback
  }

  const logoValue = get<string>('logo', '')
  const backgroundImageValue = get<string>('backgroundImage', '')

  return {
    logoSrc: logoValue ? getAbsoluteUrl(logoValue, siteUrl) : null,
    logoSize: get<number>('logoSize', 63),
    logoPosition: get<string>('logoPosition', 'left'),
    headlineFont: get<string>('headlineFont', 'inherit'),
    headlineFontSize: get<number>('headlineFontSize', 32),
    bodyFont: get<string>('bodyFont', 'inherit'),
    bodyFontSize: get<number>('bodyFontSize', 16),
    scrubberColor: get<string>('brandPrimary', '#FFFFFF'),
    backgroundType: get<string>('backgroundType', 'color'),
    backgroundColor: get<string>('backgroundColor', '#FFFFFF'),
    backgroundImageUrl: backgroundImageValue
      ? getAbsoluteUrl(backgroundImageValue, siteUrl)
      : undefined,
  }
}
